// ===================================
// TRENCH QUEST - Token Data
// Popular Solana ecosystem tokens
// ===================================

export interface TokenData {
  symbol: string
  name: string
  description: string
  basePrice: number // Starting price in USDC
  volatility: number // 0-1, affects price swings
  rugRisk: number // 0-1, chance of going to zero
  category: 'stable' | 'blue_chip' | 'mid_cap' | 'degen' | 'ultra_degen'
  lore: string // Backstory for the AI to use
  maxSupply?: number
}

// USDC is the base currency - players start with this
export const USDC: TokenData = {
  symbol: 'USDC',
  name: 'USD Coin',
  description: 'The stablecoin for normies who fear volatility',
  basePrice: 1.0,
  volatility: 0.001,
  rugRisk: 0,
  category: 'stable',
  lore: 'The safe haven. Paper hands park their gains here. Real degens see it as a temporary stop.',
}

// Other stables
export const STABLES: TokenData[] = [
  {
    symbol: 'USDT',
    name: 'Tether',
    description: 'The controversial stablecoin everyone uses anyway',
    basePrice: 1.0,
    volatility: 0.002,
    rugRisk: 0.001,
    category: 'stable',
    lore: 'Tether. Fully backed. Trust us bro. Despite the FUD, it keeps chugging along.',
  },
]

// Blue chips - the majors
export const BLUE_CHIPS: TokenData[] = [
  {
    symbol: 'SOL',
    name: 'Solana',
    description: 'The fastest chain. Sometimes it even stays online.',
    basePrice: 180,
    volatility: 0.15,
    rugRisk: 0.001,
    category: 'blue_chip',
    lore: 'The backbone of the Solana ecosystem. Toly built this. Network goes down sometimes but we don\'t talk about that.',
  },
  {
    symbol: 'JUP',
    name: 'Jupiter',
    description: 'The aggregator of aggregators. Meow.',
    basePrice: 1.20,
    volatility: 0.20,
    rugRisk: 0.01,
    category: 'blue_chip',
    lore: 'Founded by the legendary cat-loving Meow. Every swap on Solana goes through Jupiter. The infrastructure play.',
  },
  {
    symbol: 'RAY',
    name: 'Raydium',
    description: 'The OG Solana DEX. Where liquidity pools go to party.',
    basePrice: 4.50,
    volatility: 0.25,
    rugRisk: 0.02,
    category: 'blue_chip',
    lore: 'The granddaddy of Solana DeFi. Concentrated liquidity, yield farming, the whole nine yards.',
  },
  {
    symbol: 'PYTH',
    name: 'Pyth Network',
    description: 'Oracle vibes. Data feeds for the blockchain.',
    basePrice: 0.45,
    volatility: 0.22,
    rugRisk: 0.01,
    category: 'blue_chip',
    lore: 'The oracle that powers DeFi. When Pyth goes down, liquidations go wild. Infrastructure is boring but profitable.',
  },
  {
    symbol: 'JTO',
    name: 'Jito',
    description: 'MEV on Solana. Validators love this one.',
    basePrice: 3.20,
    volatility: 0.25,
    rugRisk: 0.01,
    category: 'blue_chip',
    lore: 'Liquid staking meets MEV. The sophisticated play for those who understand Solana\'s validator economics.',
  },
  {
    symbol: 'RNDR',
    name: 'Render',
    description: 'GPU rendering on the blockchain. AI narrative adjacent.',
    basePrice: 8.50,
    volatility: 0.28,
    rugRisk: 0.01,
    category: 'blue_chip',
    lore: 'Decentralized GPU power. Rides the AI wave. Actually has real use cases which is rare in crypto.',
  },
  {
    symbol: 'HNT',
    name: 'Helium',
    description: 'The people\'s network. IoT meets crypto.',
    basePrice: 6.80,
    volatility: 0.26,
    rugRisk: 0.02,
    category: 'blue_chip',
    lore: 'Migrated to Solana. Decentralized wireless infrastructure. The DePIN narrative leader.',
  },
  {
    symbol: 'W',
    name: 'Wormhole',
    description: 'Cross-chain bridging. Connecting all the chains.',
    basePrice: 0.55,
    volatility: 0.24,
    rugRisk: 0.02,
    category: 'blue_chip',
    lore: 'The bridge that survived multiple exploits and kept building. Cross-chain infrastructure play.',
  },
]

// Mid caps - established but more volatile
export const MID_CAPS: TokenData[] = [
  {
    symbol: 'BONK',
    name: 'Bonk',
    description: 'The dog coin that actually made it. Much wow.',
    basePrice: 0.000025,
    volatility: 0.35,
    rugRisk: 0.03,
    category: 'mid_cap',
    lore: 'Community coin airdropped to Solana enjoyers. The good boy of crypto. Has actual utility now somehow.',
  },
  {
    symbol: 'WIF',
    name: 'dogwifhat',
    description: 'It\'s a dog. With a hat. What more do you need?',
    basePrice: 2.80,
    volatility: 0.40,
    rugRisk: 0.05,
    category: 'mid_cap',
    lore: 'The hat-wearing Shiba that captured CT\'s heart. Ansem shilled this at $0.01. You didn\'t listen.',
  },
  {
    symbol: 'PENGU',
    name: 'Pudgy Penguins',
    description: 'NFT project turned token. Penguins taking over.',
    basePrice: 0.035,
    volatility: 0.38,
    rugRisk: 0.04,
    category: 'mid_cap',
    lore: 'From JPEGs to tokens. Luca turned a dead NFT project into a cultural phenomenon. Waddle waddle.',
  },
  {
    symbol: 'ORCA',
    name: 'Orca',
    description: 'The friendly whale of Solana DEXes.',
    basePrice: 4.20,
    volatility: 0.30,
    rugRisk: 0.03,
    category: 'mid_cap',
    lore: 'User-friendly DEX with concentrated liquidity. The approachable DeFi play.',
  },
  {
    symbol: 'MNGO',
    name: 'Mango Markets',
    description: 'Perps on Solana. What could go wrong?',
    basePrice: 0.045,
    volatility: 0.35,
    rugRisk: 0.08,
    category: 'mid_cap',
    lore: 'Survived an infamous exploit. Still building. The perps degen playground.',
  },
  {
    symbol: 'MOBILE',
    name: 'Helium Mobile',
    description: 'DePIN for mobile networks.',
    basePrice: 0.0018,
    volatility: 0.32,
    rugRisk: 0.04,
    category: 'mid_cap',
    lore: 'Part of the Helium ecosystem. Building decentralized 5G. The future of connectivity or vaporware?',
  },
  {
    symbol: 'SAMO',
    name: 'Samoyedcoin',
    description: 'The fluffiest dog on Solana.',
    basePrice: 0.012,
    volatility: 0.38,
    rugRisk: 0.05,
    category: 'mid_cap',
    lore: 'The original Solana dog coin. Fluffy vibes. OG community that survived multiple cycles.',
  },
  {
    symbol: 'DRIFT',
    name: 'Drift Protocol',
    description: 'Perps trading on Solana.',
    basePrice: 0.85,
    volatility: 0.32,
    rugRisk: 0.04,
    category: 'mid_cap',
    lore: 'Decentralized perps. Leverage trading for degens who like living on the edge.',
  },
  {
    symbol: 'RENDER',
    name: 'Render Token',
    description: 'Decentralized GPU rendering network.',
    basePrice: 7.50,
    volatility: 0.30,
    rugRisk: 0.02,
    category: 'mid_cap',
    lore: 'GPU compute for the AI age. Actually has customers paying for rendering services.',
  },
  {
    symbol: 'MSOL',
    name: 'Marinade Staked SOL',
    description: 'Liquid staking done right.',
    basePrice: 195,
    volatility: 0.16,
    rugRisk: 0.02,
    category: 'mid_cap',
    lore: 'Stake your SOL, get mSOL. DeFi composability. The responsible degen choice.',
  },
  {
    symbol: 'BSOL',
    name: 'BlazeStake SOL',
    description: 'Another liquid staking option.',
    basePrice: 190,
    volatility: 0.16,
    rugRisk: 0.02,
    category: 'mid_cap',
    lore: 'Community-driven liquid staking. Decentralization maximalists love this one.',
  },
]

// Degen plays - high volatility, cultural relevance
export const DEGEN_TOKENS: TokenData[] = [
  {
    symbol: 'FARTCOIN',
    name: 'FartCoin',
    description: 'The most immature investment you\'ll ever make. Also up 10000%.',
    basePrice: 0.85,
    volatility: 0.55,
    rugRisk: 0.12,
    category: 'degen',
    lore: 'Launched as a joke. Now has a $500M market cap. We live in a simulation.',
  },
  {
    symbol: 'GOAT',
    name: 'Goatseus Maximus',
    description: 'AI agent memecoin. The GOAT of memes. Don\'t google the name.',
    basePrice: 0.45,
    volatility: 0.50,
    rugRisk: 0.10,
    category: 'degen',
    lore: 'Spawned from the AI agent meta. Terminal of Truths approved. Absolutely unhinged community.',
  },
  {
    symbol: 'POPCAT',
    name: 'Popcat',
    description: 'Pop pop pop. The clicking cat conquers crypto.',
    basePrice: 1.20,
    volatility: 0.45,
    rugRisk: 0.08,
    category: 'degen',
    lore: 'Based on the viral clicking cat meme. Surprisingly resilient. Cat coins always find a way.',
  },
  {
    symbol: 'MEW',
    name: 'cat in a dogs world',
    description: 'The cat fighting back against dog coin dominance.',
    basePrice: 0.008,
    volatility: 0.48,
    rugRisk: 0.10,
    category: 'degen',
    lore: 'A cat in a dogs world. The eternal struggle. Cat coin maximalists swear by this one.',
  },
  {
    symbol: 'MOTHER',
    name: 'Mother Iggy',
    description: 'Iggy Azalea\'s memecoin. Celebrity coins never go wrong, right?',
    basePrice: 0.12,
    volatility: 0.52,
    rugRisk: 0.18,
    category: 'degen',
    lore: 'When a celebrity launches a coin, you know what time it is. MOTHER knows best.',
  },
  {
    symbol: 'ORE',
    name: 'Ore',
    description: 'Proof of work mining on Solana. Digital gold rush.',
    basePrice: 1.80,
    volatility: 0.42,
    rugRisk: 0.08,
    category: 'degen',
    lore: 'CPU mining on Solana. Bringing back the OG mining vibes. Miners are degens with extra steps.',
  },
  {
    symbol: 'TUNA',
    name: 'SolTuna',
    description: 'Fish coin supremacy. Swimming against the current.',
    basePrice: 0.025,
    volatility: 0.50,
    rugRisk: 0.12,
    category: 'degen',
    lore: 'The tuna that swims in Solana waters. Part of the increasingly bizarre animal coin meta.',
  },
  {
    symbol: 'SLERF',
    name: 'Slerf',
    description: 'The coin that was accidentally rugged by its own dev.',
    basePrice: 0.35,
    volatility: 0.55,
    rugRisk: 0.15,
    category: 'degen',
    lore: 'Dev burned LP by mistake. Community said "we are all Slerf" and pumped it anyway. Peak degen.',
  },
  {
    symbol: 'BOME',
    name: 'Book of Meme',
    description: 'The meme bible. Presale legend.',
    basePrice: 0.012,
    volatility: 0.48,
    rugRisk: 0.12,
    category: 'degen',
    lore: 'Darkfarms presale that went viral. BOME believers made generational wealth in days.',
  },
  {
    symbol: 'WEN',
    name: 'Wen',
    description: 'Wen moon? Wen lambo? Just WEN.',
    basePrice: 0.00015,
    volatility: 0.45,
    rugRisk: 0.10,
    category: 'degen',
    lore: 'The answer to every question in crypto is WEN. Jupiter airdrop meta coin.',
  },
  {
    symbol: 'MYRO',
    name: 'Myro',
    description: 'Raj\'s dog. The Solana co-founder\'s pet turned memecoin.',
    basePrice: 0.15,
    volatility: 0.45,
    rugRisk: 0.08,
    category: 'degen',
    lore: 'Named after Raj\'s actual dog. Insider dog coin? Maybe. Cute dog? Definitely.',
  },
  {
    symbol: 'PONKE',
    name: 'Ponke',
    description: 'Deranged monkey energy.',
    basePrice: 0.55,
    volatility: 0.50,
    rugRisk: 0.12,
    category: 'degen',
    lore: 'The monkey that represents pure degen energy. Ponke holders are a different breed.',
  },
  {
    symbol: 'MOODENG',
    name: 'Moo Deng',
    description: 'The viral baby hippo from Thailand.',
    basePrice: 0.25,
    volatility: 0.52,
    rugRisk: 0.15,
    category: 'degen',
    lore: 'A baby hippo went viral, crypto made a coin. This is the way.',
  },
  {
    symbol: 'PNUT',
    name: 'Peanut the Squirrel',
    description: 'RIP Peanut. Gone but not forgotten.',
    basePrice: 0.45,
    volatility: 0.55,
    rugRisk: 0.15,
    category: 'degen',
    lore: 'Memorial coin for the famous squirrel. Emotional trading at its finest.',
  },
  {
    symbol: 'GIGA',
    name: 'Giga Chad',
    description: 'The ultimate Chad energy token.',
    basePrice: 0.025,
    volatility: 0.48,
    rugRisk: 0.12,
    category: 'degen',
    lore: 'Based on the Gigachad meme. For those who refuse to be beta.',
  },
  {
    symbol: 'TRUMP',
    name: 'MAGA',
    description: 'Political memecoin. Very controversial. Much pump.',
    basePrice: 35.00,
    volatility: 0.60,
    rugRisk: 0.15,
    category: 'degen',
    lore: 'Political memecoins are a thing now. Trade at your own risk. Not financial or political advice.',
  },
  {
    symbol: 'AI16Z',
    name: 'ai16z',
    description: 'AI agents meet venture capital memes.',
    basePrice: 1.50,
    volatility: 0.50,
    rugRisk: 0.12,
    category: 'degen',
    lore: 'AI agent token playing on the a16z VC brand. The future of autonomous agents or elaborate LARP?',
  },
  {
    symbol: 'ZEREBRO',
    name: 'Zerebro',
    description: 'Another AI agent token. The meta is strong.',
    basePrice: 0.45,
    volatility: 0.52,
    rugRisk: 0.15,
    category: 'degen',
    lore: 'AI agents creating and trading tokens. We\'re living in the future.',
  },
  {
    symbol: 'GRIFFAIN',
    name: 'Griffain',
    description: 'AI agent infrastructure.',
    basePrice: 0.28,
    volatility: 0.48,
    rugRisk: 0.12,
    category: 'degen',
    lore: 'Building infrastructure for AI agents on Solana. The picks and shovels play.',
  },
  {
    symbol: 'ACT',
    name: 'Act I: The AI Prophecy',
    description: 'AI narrative token with theatrical flair.',
    basePrice: 0.35,
    volatility: 0.50,
    rugRisk: 0.14,
    category: 'degen',
    lore: 'Part of the AI agent meta. The prophecy unfolds.',
  },
  {
    symbol: 'CHILLGUY',
    name: 'Chill Guy',
    description: 'Just a chill guy. Vibes only.',
    basePrice: 0.35,
    volatility: 0.48,
    rugRisk: 0.12,
    category: 'degen',
    lore: 'The meme that represents not caring about price action. Ironically pumps hard.',
  },
  {
    symbol: 'LOCKIN',
    name: 'Lock In',
    description: 'Time to lock in and focus.',
    basePrice: 0.08,
    volatility: 0.45,
    rugRisk: 0.10,
    category: 'degen',
    lore: 'Motivational memecoin. Lock in, stop scrolling, make money.',
  },
  {
    symbol: 'DADDY',
    name: 'Daddy Tate',
    description: 'Andrew Tate adjacent memecoin.',
    basePrice: 0.15,
    volatility: 0.55,
    rugRisk: 0.18,
    category: 'degen',
    lore: 'Top G energy. Controversial but the chart doesn\'t care about your feelings.',
  },
  {
    symbol: 'BRETT',
    name: 'Brett',
    description: 'Boy\'s Club character turned token.',
    basePrice: 0.12,
    volatility: 0.45,
    rugRisk: 0.10,
    category: 'degen',
    lore: 'From the Pepe comics. Brett is the friend who\'s always there.',
  },
  {
    symbol: 'SPX',
    name: 'SPX6900',
    description: 'S&P but make it degen.',
    basePrice: 0.85,
    volatility: 0.48,
    rugRisk: 0.12,
    category: 'degen',
    lore: 'Murad\'s cult coin. SPX to 6900 is not a meme, it\'s a lifestyle.',
  },
  {
    symbol: 'TREMP',
    name: 'Tremp',
    description: 'The other political memecoin.',
    basePrice: 0.45,
    volatility: 0.55,
    rugRisk: 0.15,
    category: 'degen',
    lore: 'Political meme season never ends. TREMP or dump.',
  },
  {
    symbol: 'COST',
    name: 'Costco Hot Dog',
    description: '$1.50 forever. The hot dog that never inflates.',
    basePrice: 0.008,
    volatility: 0.42,
    rugRisk: 0.10,
    category: 'degen',
    lore: 'Based on the legendary $1.50 Costco hot dog that defies inflation. A true store of value.',
  },
]

// Ultra degen - maximum risk, maximum potential
export const ULTRA_DEGEN_TOKENS: TokenData[] = [
  {
    symbol: 'VINE',
    name: 'Vine',
    description: 'Remember Vine? Neither does anyone else.',
    basePrice: 0.015,
    volatility: 0.70,
    rugRisk: 0.35,
    category: 'ultra_degen',
    lore: 'Nostalgia play. 6 second videos, 6 figure losses.',
  },
  {
    symbol: 'WOJAK',
    name: 'Wojak Coin',
    description: 'The face of every trader who bought the top.',
    basePrice: 0.0005,
    volatility: 0.70,
    rugRisk: 0.35,
    category: 'ultra_degen',
    lore: 'The meme that describes the pain. Pink wojak when down, chad wojak when up. Mostly pink.',
  },
  {
    symbol: 'RETARDIO',
    name: 'Retardio',
    description: 'The name says it all.',
    basePrice: 0.08,
    volatility: 0.75,
    rugRisk: 0.40,
    category: 'ultra_degen',
    lore: 'Self-aware degen energy. If you buy this, you know what you\'re getting into.',
  },
  {
    symbol: 'SIGMA',
    name: 'Sigma',
    description: 'Sigma grindset token.',
    basePrice: 0.05,
    volatility: 0.65,
    rugRisk: 0.30,
    category: 'ultra_degen',
    lore: 'For those who walk alone. Sigma males don\'t need your validation or your liquidity.',
  },
  {
    symbol: 'BODEN',
    name: 'Jeo Boden',
    description: 'Political parody coin.',
    basePrice: 0.08,
    volatility: 0.60,
    rugRisk: 0.25,
    category: 'ultra_degen',
    lore: 'Political meme season. Falls asleep mid-pump.',
  },
  {
    symbol: 'FWOG',
    name: 'Fwog',
    description: 'Frog coin with an impediment.',
    basePrice: 0.015,
    volatility: 0.55,
    rugRisk: 0.25,
    category: 'ultra_degen',
    lore: 'Not Pepe but definitely frog adjacent. Fwog goes brrr.',
  },
  {
    symbol: 'LUIGI',
    name: 'Luigi',
    description: 'Player 2 energy.',
    basePrice: 0.02,
    volatility: 0.60,
    rugRisk: 0.28,
    category: 'ultra_degen',
    lore: 'Always second place, until he\'s not. Luigi season is real.',
  },
  {
    symbol: 'HARRY',
    name: 'Harry Bolz',
    description: 'Internet character turned token.',
    basePrice: 0.003,
    volatility: 0.65,
    rugRisk: 0.35,
    category: 'ultra_degen',
    lore: 'From meme to money. Harry holders have diamond conviction.',
  },
  {
    symbol: 'BUTTHOLE',
    name: 'Butthole',
    description: 'Yes, this is real. Yes, people buy it.',
    basePrice: 0.0008,
    volatility: 0.80,
    rugRisk: 0.45,
    category: 'ultra_degen',
    lore: 'The most absurd entry on this list. And yet, someone made money.',
  },
  {
    symbol: 'HAWK',
    name: 'Hawk Tuah',
    description: 'Viral meme turned token. You know the one.',
    basePrice: 0.002,
    volatility: 0.75,
    rugRisk: 0.50,
    category: 'ultra_degen',
    lore: 'Celebrity meme coin that went about as well as expected. Spit on that chart.',
  },
  {
    symbol: 'CATWIF',
    name: 'Cat Wif Hat',
    description: 'Cat version of the dog wif hat.',
    basePrice: 0.0001,
    volatility: 0.65,
    rugRisk: 0.35,
    category: 'ultra_degen',
    lore: 'Derivative play. If dog wif hat works, why not cat? Few understand.',
  },
  {
    symbol: 'GME',
    name: 'GME on Solana',
    description: 'GameStop but make it Solana.',
    basePrice: 0.005,
    volatility: 0.70,
    rugRisk: 0.30,
    category: 'ultra_degen',
    lore: 'Roaring Kitty energy on Solana. The apes found a new chain.',
  },
  {
    symbol: 'USA',
    name: 'American Coin',
    description: 'Patriotic memecoin.',
    basePrice: 0.015,
    volatility: 0.55,
    rugRisk: 0.25,
    category: 'ultra_degen',
    lore: 'Freedom isn\'t free, but this token might be worthless.',
  },
  {
    symbol: 'PEPE',
    name: 'Pepe (Solana)',
    description: 'The frog that started it all. Solana edition.',
    basePrice: 0.0001,
    volatility: 0.50,
    rugRisk: 0.20,
    category: 'ultra_degen',
    lore: 'Every chain needs a Pepe. This is Solana\'s version.',
  },
  {
    symbol: 'DOGE',
    name: 'Doge (Solana)',
    description: 'Solana\'s version of the OG memecoin.',
    basePrice: 0.0005,
    volatility: 0.45,
    rugRisk: 0.20,
    category: 'ultra_degen',
    lore: 'Much wow. Very Solana. Such derivative.',
  },
  {
    symbol: 'SHIB',
    name: 'Shiba (Solana)',
    description: 'Shiba Inu on Solana.',
    basePrice: 0.00001,
    volatility: 0.50,
    rugRisk: 0.22,
    category: 'ultra_degen',
    lore: 'The Shiba that migrated to faster pastures.',
  },
  {
    symbol: 'CWIF',
    name: 'Catwifhat',
    description: 'Another cat with headwear.',
    basePrice: 0.00005,
    volatility: 0.60,
    rugRisk: 0.30,
    category: 'ultra_degen',
    lore: 'The hat meta extends to cats. Truly the future of finance.',
  },
  {
    symbol: 'MICHI',
    name: 'Michi',
    description: 'The cat that Mert loves.',
    basePrice: 0.12,
    volatility: 0.50,
    rugRisk: 0.18,
    category: 'ultra_degen',
    lore: 'Helius founder adjacent. Cat coin with infrastructure connections.',
  },
  {
    symbol: 'NEIRO',
    name: 'Neiro',
    description: 'The new Kabosu. Doge successor.',
    basePrice: 0.0015,
    volatility: 0.55,
    rugRisk: 0.25,
    category: 'ultra_degen',
    lore: 'Named after the dog that succeeded Kabosu. The next generation.',
  },
  {
    symbol: 'GOATSEUS',
    name: 'Goatseus',
    description: 'The original GOAT branding.',
    basePrice: 0.02,
    volatility: 0.60,
    rugRisk: 0.30,
    category: 'ultra_degen',
    lore: 'IYKYK. If you don\'t, don\'t search.',
  },
]

// All tokens combined
export const ALL_TOKENS: TokenData[] = [
  USDC,
  ...STABLES,
  ...BLUE_CHIPS,
  ...MID_CAPS,
  ...DEGEN_TOKENS,
  ...ULTRA_DEGEN_TOKENS,
]

// Get token by symbol
export const getToken = (symbol: string): TokenData | undefined => {
  return ALL_TOKENS.find(t => t.symbol === symbol)
}

// Get available tokens (all tokens available from start)
export const getAvailableTokens = (_day: number): TokenData[] => {
  return ALL_TOKENS
}

// Get tokens by category
export const getTokensByCategory = (category: TokenData['category']): TokenData[] => {
  return ALL_TOKENS.filter(t => t.category === category)
}

// Get random token by category
export const getRandomTokenByCategory = (category: TokenData['category']): TokenData | undefined => {
  const tokens = getTokensByCategory(category)
  if (tokens.length === 0) return undefined
  return tokens[Math.floor(Math.random() * tokens.length)]
}

// Search tokens by name or symbol
export const searchTokens = (query: string): TokenData[] => {
  const q = query.toLowerCase()
  return ALL_TOKENS.filter(t =>
    t.symbol.toLowerCase().includes(q) ||
    t.name.toLowerCase().includes(q)
  )
}
